/* 2026-07 -- Pre-order support
 * - syncCardState now reads policy/quantity/ship (STOQ shipping_text metafield,
 *   stamped by the card snippet) off the selected option / variants JSON and
 *   stores activePreorder + activeShip on the card dataset.
 * - updateCardPreorderFlag() drives the per-card [data-*-preorder-flag] element.
 * - buildStagedEntry carries preorder/ship per staged item; renderSidebar
 *   aggregates them (LATEST ship date wins, same rule as the PDP kit message),
 *   appends a pre-order line under the sidebar items, and switches the CTA
 *   label to "Pre-order N items".
 * - addToCart adds a "Pre-order: Ships from {date}" line-item property,
 *   matching STOQ's PDP wording exactly so all add paths read the same.
 * - Canonical pre-order test everywhere: inventory_policy == 'continue' AND
 *   inventory_quantity <= 0 -- matches zlIsPreorder() on the 2026 PDP.
 */
/**
 * kit-builder.js
 * Zoomlite Kit Builder - v2.5
 *
 * Forked from bundle-builder.js v1.9. The staged cart model, qualifies() gate,
 * add-to-cart path, sidebar, mobile bar and flat pricing are inherited verbatim.
 * The kit builder adds the set layout on top; the live bundle builder is untouched.
 *
 * CHANGELOG
 * v2.5 - 2026-07: Continuous cycling on the set slot arrows.
 *                 - show() wraps the index past either end instead of
 *                   clamping, so next from the last product lands on the
 *                   first and prev from the first lands on the last. Applies
 *                   to the stage arrows, the config card arrows, and clicks
 *                   on the peeked stage items (all route through show()).
 *                 - renderStage() wraps the peek positions when a group has
 *                   three or more products, so the last product peeks beside
 *                   the first and both peeks are always present (the empty-
 *                   side asymmetry from the v2.2 alignment fix is gone).
 *                   Two products keep the one-side peek but the arrows wrap.
 *                 - Arrows now disable only when a group has a single
 *                   product; end-of-list disabling is removed.
 * v2.4 - 2026-07: Product quick-look modal (set mode).
 *                 - initProductModal(): delegated click on the card details
 *                   links (data-kb-modal-src). openProductModal() fetches the
 *                   ?view=kit-modal alternate view for the card's current
 *                   variant (card.dataset.activeVariantId), caches per URL,
 *                   injects into the shared zl-modal shell, appends the
 *                   shipping template, and sets the sheet title from the
 *                   card. Loading state while in flight; fetch failure shows
 *                   a full-page link. Missing shell or zl-modal.js falls
 *                   through to plain navigation.
 *                 - The sticky bar (z-index above the sheet) is suppressed
 *                   with the existing kb-mobile-bar--suppressed class while
 *                   the sheet is open; a MutationObserver on the sheet's
 *                   class restores it on any close path (overlay, X, Esc,
 *                   swipe).
 * v2.3 - 2026-07: Per-product session key, dead summary code removed.
 *                 - sessionStorage key now includes data-product-id from the
 *                   section (this.storageKey). The section id is identical for
 *                   every product on one template, so multiple metafield-driven
 *                   kits would otherwise share one staged-state bucket and a
 *                   selection on kit A would restore into kit B. Page-context
 *                   builders (no product) keep the old section-only key, so
 *                   nothing changes for them.
 *                 - Removed renderPageSummary(), its seven call sites, the
 *                   KitPageSummary class and its boot hook. They served the
 *                   bundle builder's page summary section, which does not
 *                   exist in the kit fork; the class listened for an event on
 *                   pages that can never contain its target. The bundle
 *                   builder's own files are untouched.
 * v2.2 - 2026-07: Footer button label restored after an add.
 *                 - addToCart writes "Adding..." onto every finalise button.
 *                   The panels rebuild their labels in renderSidebar and the
 *                   mobile button is rewritten explicitly, but the footer
 *                   button only had its state toggled, so "Adding..." stuck
 *                   after a successful add. renderSidebar now captures the
 *                   footer's merchant label from Liquid once (data-label) and
 *                   restores it on every render. Both success paths reach
 *                   renderSidebar (clearBundle always, stageActive when
 *                   preselect refills), so the label always resets.
 * v2.1 - 2026-07: 3-up stage, click-to-select peeks, in-flow footer.
 *                 - SetSlot now drives a kb-stage-cell holding one kb-stage-item
 *                   per product (all server-rendered). renderStage() assigns
 *                   --prev / --active / --next position classes, mirrors the
 *                   visible items' images from their cards (so variant colour
 *                   changes show on the stage), and disables the arrows at the
 *                   ends. Clicking a peeked item steps to it (event delegation
 *                   on the track, read the class at click time).
 *                 - show() clamps instead of wrapping: no loop-around at either
 *                   end. Dots still jump directly. Two products peek on one
 *                   side only; one product renders alone, arrows disabled.
 *                 - renderSidebar() writes the bundled total (or the guidance
 *                   line while unqualified) into .kb-set-footer-total and drives
 *                   the footer finalise button's disabled/ready state. The
 *                   button keeps its merchant label; guidance lives in the
 *                   total line. Click binding and the press-invert loading lock
 *                   come free via the shared .kb-finalise-btn class.
 *                 - config.setLayout removed; data-set-mode alone drives set
 *                   behaviour. The engine (gate, staged cart, add path) is
 *                   untouched.
 * v2.0 - 2026-07: Set layouts (vertical Away / horizontal Dagne).
 *                 - New config from section data attrs: setMode, setLayout,
 *                   setPreselect.
 *                 - New SetSlot class: each Product Group renders as a single
 *                   product at a time with a prev/next cycler and dots. Cycling
 *                   or changing colour stages exactly ONE variant per group
 *                   (single-select): stageActive() clears the group's prior
 *                   staged entry before adding the new one, so a slot can never
 *                   hold two products.
 *                 - setPreselect (default on) fills each slot on load so the kit
 *                   reads complete and the bundled price shows immediately.
 *                 - Session restore moves each slot to its stored product and
 *                   variant (syncFromStaged). After a successful add, slots
 *                   re-fill so the kit stays complete.
 *                 - The gate, staged cart and cart-add path are unchanged: they
 *                   read staged state and group roles, not the layout.
 * v1.9 - 2026-07: Flat pricing display support (config.flatDisplay).
 *                 - syncCardState, in flat mode, sets the card current price to the
 *                   discounted bundle price and writes the struck original into
 *                   .kb-card-price-original, recomputing both on variant change.
 *                 - The bottom bar, in flat mode once qualified, renders the
 *                   bundled total: discounted price, struck original, percent tag,
 *                   with a --summary class so the caption clamp is dropped. The
 *                   "Bundled total:" label hides on mobile via CSS. Pre-qualify it
 *                   still shows the guidance, and non-flat pages are unchanged.
 *                 - Discount stays display-only; Regios applies the real reduction.
 * v1.8 - 2026-07: Clear the loading class on a successful add.
 *                 - The success path cleared staged state but left
 *                   kb-finalise-btn--loading on the buttons. Harmless before,
 *                   since :disabled masked it, but the CSS press invert makes that
 *                   class visible as a white button plus spinner. The success path
 *                   now removes it, matching the error path, so the reset button
 *                   never sits inverted after the drawer opens.
 * v1.7 - 2026-07: Empty-state prompt now shows on mobile.
 *                 - The mobile bottom bar button kept its own short label and
 *                   never showed the per-group prompt, so on a phone the guidance
 *                   only appeared inside the tap-to-open drawer. renderSidebar now
 *                   routes the guidance (ctaText) into the mobile bar info line
 *                   while the bundle is incomplete, so the shopper sees "Add your
 *                   carry-on to continue" without opening anything. Once it
 *                   qualifies, the info line returns to the price. The compact
 *                   button stays "Add to cart" / "Add N items to cart".
 * v1.6 - 2026-07: Immediate click feedback on finalise.
 *                 - addToCart sets the finalise buttons to "Adding..." on click,
 *                   so the wait for /cart/add.js reads as a deliberate save rather
 *                   than dead air. The drawer still opens on success, so an empty
 *                   cart never flashes on a bundle page.
 *                 - On error, renderSidebar restores the real button labels and
 *                   enabled state instead of leaving "Adding..." stuck.
 * v1.5 - 2026-07: Per-group full-sentence empty-state button prompt.
 *                 - Reads data-group-prompt off each base group (the per-group
 *                   "Empty-state button prompt" field in the schema).
 *                 - New config flag designMode, from data-design-mode.
 *                 - New helpers firstMissingBaseGroup and missingBaseButtonText.
 *                   The summary button, while a base group is empty, shows the
 *                   group's full prompt sentence verbatim. Blank prompt shows a
 *                   flag "[Set prompt: Label]" inside the theme editor so a new
 *                   person fills it, and falls back to "Add your Label to
 *                   continue" on the live storefront.
 *                 - The progress line under the bar keeps its short auto text.
 * Architecture: Staged cart model
 * - Items staged in JS state, never in cart until "Add to Cart" fires
 * - One POST to /cart/add.js with full items[] array
 * - Dispatches cart:open to trigger Unicorn drawer
 * - Each section instance is fully independent
 * - Page-level summary section reads all instances
 *
 * CHANGELOG
 * v1.4 - 2026-07: One shared qualification rule for the button and the discount.
 *                 - Staged entries now carry groupId, read from the card
 *                   data-group-id attribute (set per product group).
 *                 - New helpers: groupFilled, getBaseGroupIds, hasGroups,
 *                   hasAddonGroups, baseGroupsAllFilled, firstMissingBaseLabel,
 *                   qualifies.
 *                 - qualifies(): every Base group must hold at least one item,
 *                   and if any Add-on groups exist, at least one Add-on item is
 *                   staged. Both canAddToCart and qualifiesForDiscount now read
 *                   qualifies() in flat mode, so the button and the discount
 *                   preview unlock together. Two Base groups need one item from
 *                   each. One Base plus Add-on groups needs the base plus one
 *                   add-on from anywhere.
 *                 - Legacy flat pages with no groups keep the old single-base
 *                   rule. Quantity-tiers mode is unchanged.
 *                 - CTA and progress name the missing Base group first, then the
 *                   add-on step. Progress bar spans every base group plus the
 *                   add-on step.
 * v1.3 - 2026-07: Base plus add-on discount model and group roles.
 *                 - Staged entries now carry role (base or add-on), read from the
 *                   card data-group-role attribute (set per product group).
 *                 - New config: discountModel, flatPct, baseNoun (from section data attrs).
 *                 - New helpers: getBaseQty, getAddonQty, hasBase, qualifiesForDiscount,
 *                   getEffectiveDiscountPct, canAddToCart.
 *                 - renderSidebar and the page summary use getEffectiveDiscountPct.
 *                   In base_addon_flat mode the flat percentage applies only when a base
 *                   item plus one add-on are staged, and Add to Cart holds until a base
 *                   item is present. Quantity-tiers mode is unchanged.
 *                 - Cart line items now also carry _bundle_role for a future discount Function.
 * v1.2 - 2026-07: Hide the page mobile bundle bar while a colour modal is open,
 *                 so the modal footer (item count + Confirm) stays visible on mobile.
 *                 Adds this.mobileBar in the ColourPickerModal constructor and toggles
 *                 .kb-mobile-bar--suppressed in open(), close(), and closeAll().
 * v1.1 — Fix: Confirm button no longer disabled when all units removed to zero
 *         Button label changes to "Remove All" when totalQty === 0
 *         Unit count added to trigger button label alongside colour count
 */

(function () {
  'use strict';

  // ─── Page-level registry so the summary section can read all instances ───
  window.KitBuilderInstances = window.KitBuilderInstances || [];

  // ─── Utility: format money (cents → "$X.XX") ───────────────────────────
  // Pre-order (2026-07): parse STOQ dates like "28th May 2026" for compare.
  function parseShipDate(str) {
    if (!str) return 0;
    var ts = Date.parse(String(str).replace(/(\d+)(st|nd|rd|th)/, '$1'));
    return isNaN(ts) ? 0 : ts;
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }


  // ═══════════════════════════════════════════════════════════════════════
  // KitBuilder — one instance per section
  // ═══════════════════════════════════════════════════════════════════════
  class KitBuilder {
    constructor(sectionEl) {
      this.section = sectionEl;
      this.sectionId = sectionEl.dataset.sectionId;
      // Per-product namespace for sessionStorage. The section id is identical
      // for every product on the same template, so without the product id all
      // kits would share one staged-state bucket and selections would leak
      // between kits. Non-product contexts (pages) fall back to section only.
      this.storageKey = 'kb-staged-' + this.sectionId +
        (sectionEl.dataset.productId ? '-' + sectionEl.dataset.productId : '');

      // Config from data attributes (written by Liquid)
      this.config = {
        minItems: parseInt(sectionEl.dataset.minItems || 0),
        maxItems: parseInt(sectionEl.dataset.maxItems || 999),
        tiers: JSON.parse(sectionEl.dataset.tiers || '[]'),
        collectionHandle: sectionEl.dataset.collectionHandle || '',
        bundleProperty: sectionEl.dataset.bundleProperty || '_Bundle',
        discountModel: sectionEl.dataset.discountModel || 'quantity_tiers',
        flatPct: parseInt(sectionEl.dataset.flatPct || 0),
        baseNoun: sectionEl.dataset.baseNoun || 'base item',
        designMode: sectionEl.dataset.designMode === 'true',
        flatDisplay: sectionEl.dataset.flatDisplay === 'true',
        setMode: sectionEl.dataset.setMode === 'true',
        setPreselect: sectionEl.dataset.setPreselect !== 'false',
      };

      // Staged state: { variantId: { id, qty, price, title, variantTitle, image } }
      this.staged = {};

      this.init();

      // Register globally for summary section
      window.KitBuilderInstances.push(this);
    }

    // ── Initialise all card interactions ──────────────────────────────────
    init() {
      const cards = this.section.querySelectorAll('.kb-card');
      cards.forEach(card => this.initCard(card));
      this.initModals();
      this.initDrawerToggle();
      this.initAddToCart();
      // Set layouts (kit builder): single-select slots with a cycler
      if (this.config.setMode) this.initSlots();
      if (this.config.setMode) this.initProductModal();
      this.renderSidebar();
      // Restore any staged selections saved from previous page visit
      this.restoreFromSession();
    }

    // ── Initialise set-layout slots (one SetSlot per config card) ─────────
    initSlots() {
      this.slots = [];
      this.section.querySelectorAll('.kb-config-card.kb-group').forEach(groupEl => {
        this.slots.push(new SetSlot(groupEl, this));
      });
    }

    // ── Product quick-look modal (set mode) ────────────────────────────────
    // Card links carry data-kb-modal-src ({url}?view=kit-modal). Clicking one
    // fetches the alternate view for the card's current variant and injects it
    // into the shared zl-modal shell, appending the shipping block from the
    // section's template tag. Missing shell or zl-modal.js means the click
    // falls through to plain navigation (the href), same as no-JS.
    initProductModal() {
      this.pdmCache = {};
      this.section.addEventListener('click', (e) => {
        const link = e.target.closest('.kb-card-product-link');
        if (!link) return;
        const src = link.dataset.kbModalSrc;
        if (!src || typeof window.zlOpenSheet !== 'function') return;
        if (!document.getElementById('kb-pdm-' + this.sectionId)) return;
        e.preventDefault();
        this.openProductModal(src, link.closest('.kb-card'));
      });
    }

    openProductModal(src, card) {
      const id = 'kb-pdm-' + this.sectionId;
      const modal = document.getElementById(id);
      const body = modal.querySelector('.zl-modal__body');
      const titleEl = document.getElementById(id + '-title');
      if (!body) return;

      // Sheet header shows the product name the card already has.
      if (titleEl) {
        const t = card ? card.querySelector('.kb-card-title') : null;
        titleEl.textContent = t ? t.textContent.trim() : 'Product details';
      }

      // The card tracks its live selection; fetch that variant so the modal
      // image matches what the shopper picked.
      const vid = card && card.dataset.activeVariantId ? card.dataset.activeVariantId : '';
      const url = vid ? src + '&variant=' + vid : src;

      // The sticky bar out-z-indexes the sheet; suppress it while open and
      // restore when the sheet loses is-open (overlay, X, Esc, or swipe all
      // close through zl-modal.js, so watch the class rather than the click).
      const bar = this.section.querySelector('.kb-mobile-bar');
      if (bar) {
        bar.classList.add('kb-mobile-bar--suppressed');
        if (!this.pdmObserver) {
          this.pdmObserver = new MutationObserver(() => {
            if (!modal.classList.contains('is-open')) {
              const b = this.section.querySelector('.kb-mobile-bar');
              if (b) b.classList.remove('kb-mobile-bar--suppressed');
            }
          });
          this.pdmObserver.observe(modal, { attributes: true, attributeFilter: ['class'] });
        }
      }

      const shipTpl = this.section.querySelector('template[data-kb-pdm-shipping]');
      const shipHtml = shipTpl ? shipTpl.innerHTML : '';
      const finish = (html) => { body.innerHTML = html + shipHtml; };

      if (this.pdmCache[url]) {
        finish(this.pdmCache[url]);
        window.zlOpenSheet(id);
        return;
      }

      body.innerHTML = '<p class="kb-pdm-loading">Loading&hellip;</p>';
      window.zlOpenSheet(id);
      fetch(url)
        .then(r => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
        .then(html => { this.pdmCache[url] = html; finish(html); })
        .catch(() => {
          const full = src.split('?')[0];
          body.innerHTML = '<p>Could not load product details. <a href="' + full + '">View the full product page</a>.</p>';
        });
    }

    // ── Initialise colour picker modals (modal mode only) ─────────────────
    initModals() {
      this.modals = [];
      this.section.querySelectorAll('.kb-card').forEach(card => {
        if (card.querySelector('.kb-colour-modal')) {
          this.modals.push(new ColourPickerModal(card, this));
        }
      });
    }

    // ── Sync all modal trigger buttons after session restore ──────────────
    syncModalTriggers() {
      if (this.modals) {
        this.modals.forEach(m => m.syncFromStaged());
      }
    }

    // ── Per-card setup ────────────────────────────────────────────────────
    initCard(card) {
      // Variant radio inputs (Impulse pattern: data-index="option1")
      const variantInputs = card.querySelectorAll('.kb-variant-input');
      variantInputs.forEach(input => {
        input.addEventListener('change', () => this.onVariantChange(card));
      });

      // ATC button
      const atcBtn = card.querySelector('.kb-atc-btn');
      if (atcBtn) {
        atcBtn.addEventListener('click', () => this.onAddClick(card));
      }

      // Qty stepper (hidden until first add)
      const minus = card.querySelector('.kb-qty-minus');
      const plus = card.querySelector('.kb-qty-plus');
      if (minus) minus.addEventListener('click', () => this.onQtyChange(card, -1));
      if (plus) plus.addEventListener('click', () => this.onQtyChange(card, 1));

      // Set initial active variant state
      this.syncCardState(card);
    }

    // ── Variant changed → update card's active variant data ───────────────
    // Each variant is staged independently — switching colour does NOT migrate
    // qty. The stepper updates to show the newly selected variant's staged qty.
    onVariantChange(card) {
      this.syncCardState(card);
      // Update button/stepper to reflect this variant's own staged qty
      this.syncCardUI(card);
      this.renderSidebar();
      this.saveToSession();
    }

    // ── Read selected variant from card inputs and update data attrs ───────
    syncCardState(card) {
      // Try the hidden select (Impulse sets value on variant change)
      const variantSelect = card.querySelector('.kb-variant-select');
      let variantId = null;
      let variantTitle = null;
      let variantAvailable = true;
      let variantPrice = null;

      if (variantSelect) {
        const selectedOption = variantSelect.options[variantSelect.selectedIndex];
        if (selectedOption) {
          variantId = selectedOption.value;
          variantTitle = selectedOption.dataset.title || selectedOption.text;
          variantAvailable = selectedOption.dataset.available !== 'false';
          variantPrice = parseInt(selectedOption.dataset.price || card.dataset.price || 0);
          // Pre-order (2026-07): canonical test, mirrors PDP zlIsPreorder()
          card.dataset.activePreorder = (selectedOption.dataset.policy === 'continue' && parseInt(selectedOption.dataset.quantity || '0', 10) <= 0) ? 'true' : 'false';
          card.dataset.activeShip = selectedOption.dataset.ship || '';
        }
      }

      // Fallback: read checked radio inputs
      if (!variantId) {
        const checkedInputs = card.querySelectorAll('.kb-variant-input:checked');
        if (checkedInputs.length > 0) {
          // Find matching variant from the variants data
          const variantsData = JSON.parse(card.dataset.variants || '[]');
          const selectedValues = Array.from(checkedInputs).map(i => i.value);
          const match = variantsData.find(v =>
            selectedValues.every(val => v.options.includes(val))
          );
          if (match) {
            variantId = String(match.id);
            variantTitle = match.title === 'Default Title' ? '' : match.title;
            variantAvailable = match.available;
            variantPrice = match.price;
            // Pre-order (2026-07): same canonical test from variants JSON
            card.dataset.activePreorder = (match.policy === 'continue' && (match.qty || 0) <= 0) ? 'true' : 'false';
            card.dataset.activeShip = match.ship || '';
          }
        }
      }

      // Store on card element
      if (variantId) {
        card.dataset.activeVariantId = variantId;
        card.dataset.activeVariantTitle = variantTitle || '';
        card.dataset.activeVariantAvailable = variantAvailable;
        card.dataset.activeVariantPrice = variantPrice || card.dataset.price || 0;
      }

      this.updateCardPreorderFlag(card);

      // Update OOS state
      const atcBtn = card.querySelector('.kb-atc-btn');
      if (atcBtn) {
        if (!variantAvailable) {
          atcBtn.disabled = true;
          atcBtn.textContent = 'Sold Out';
          atcBtn.classList.add('kb-atc-btn--soldout');
        } else {
          atcBtn.disabled = false;
          atcBtn.classList.remove('kb-atc-btn--soldout');
          // Don't reset text if it already shows a count
          if (!card.classList.contains('kb-card--added')) {
            atcBtn.textContent = atcBtn.dataset.defaultText || 'Add to Bundle';
          }
        }
      }

      // Update displayed price — target the current price span only
      const priceCurrentEl = card.querySelector('.kb-card-price-current');
      if (priceCurrentEl && variantPrice) {
        if (this.config.flatDisplay && this.config.flatPct > 0) {
          // Flat mode: current shows the discounted bundle price, struck shows the original.
          const discounted = Math.round(variantPrice * (100 - this.config.flatPct) / 100);
          priceCurrentEl.textContent = formatMoney(discounted);
          const struckEl = card.querySelector('.kb-card-price-original');
          if (struckEl) struckEl.textContent = formatMoney(variantPrice);
        } else {
          priceCurrentEl.textContent = formatMoney(variantPrice);
        }
      }

      // Swap image to match variant if available
      if (variantId) {
        this.swapCardImage(card, variantId);
      }
    }

    // ── Swap card image to match selected variant ─────────────────────────
    swapCardImage(card, variantId) {
      const imgEl = card.querySelector('.kb-card-img');
      if (!imgEl) return;
      const variantsData = JSON.parse(card.dataset.variants || '[]');
      const variant = variantsData.find(v => String(v.id) === String(variantId));
      if (variant && variant.featured_image && variant.featured_image.src) {
        const src = variant.featured_image.src.replace(/(\.[^.]+)$/, '_400x$1');
        imgEl.src = src;
      }
    }

    // ── Add to bundle clicked ─────────────────────────────────────────────
    onAddClick(card) {
      const variantId = card.dataset.activeVariantId;
      if (!variantId) return;

      const totalQty = this.getTotalQty();
      if (totalQty >= this.config.maxItems) {
        this.flashMessage(`Maximum ${this.config.maxItems} items allowed in this bundle.`);
        return;
      }

      if (this.staged[variantId]) {
        this.staged[variantId].qty += 1;
      } else {
        this.staged[variantId] = this.buildStagedEntry(card, 1);
      }

      this.updateCardUI(card, this.staged[variantId].qty);
      this.renderSidebar();
      this.saveToSession();
    }

    // ── Qty stepper clicked ───────────────────────────────────────────────
    onQtyChange(card, delta) {
      const variantId = card.dataset.activeVariantId;
      if (!variantId || !this.staged[variantId]) return;

      const totalQty = this.getTotalQty();
      if (delta > 0 && totalQty >= this.config.maxItems) {
        this.flashMessage(`Maximum ${this.config.maxItems} items allowed.`);
        return;
      }

      this.staged[variantId].qty = Math.max(0, this.staged[variantId].qty + delta);

      if (this.staged[variantId].qty === 0) {
        delete this.staged[variantId];
        this.resetCardUI(card);
      } else {
        this.updateCardUI(card, this.staged[variantId].qty);
      }

      this.renderSidebar();
      this.saveToSession();
    }

    // ── Build a staged entry from card data ───────────────────────────────
    // ── Pre-order flag on card (2026-07) ──────────────────────────────
    updateCardPreorderFlag(card) {
      const flag = card.querySelector('[data-kb-preorder-flag]');
      if (!flag) return;
      const ship = card.dataset.activeShip || '';
      if (card.dataset.activePreorder === 'true') {
        flag.textContent = ship ? ('Pre-order — ships ' + ship) : 'Pre-order — shipping date to be confirmed';
        flag.hidden = false;
      } else {
        flag.hidden = true;
      }
    }

    buildStagedEntry(card, qty) {
      return {
        id: card.dataset.activeVariantId,
        qty: qty,
        price: parseInt(card.dataset.activeVariantPrice || 0),
        title: card.dataset.title || '',
        variantTitle: card.dataset.activeVariantTitle || '',
        image: card.querySelector('.kb-card-img')?.src || '',
        role: card.dataset.groupRole || 'addon',
        groupId: card.dataset.groupId || '',
        preorder: card.dataset.activePreorder === 'true',
        ship: card.dataset.activeShip || '',
      };
    }

    // ── Update card UI: button → stepper (qty = this variant's qty only) ──
    updateCardUI(card, qty) {
      card.classList.add('kb-card--added');
      const atcBtn = card.querySelector('.kb-atc-btn');
      const stepper = card.querySelector('.kb-stepper');
      const qtyDisplay = card.querySelector('.kb-qty-display');

      if (atcBtn) atcBtn.style.display = 'none';
      if (stepper) stepper.style.display = 'flex';
      // Show qty for THIS specific variant, not total across all variants
      if (qtyDisplay) qtyDisplay.textContent = qty;
    }

    // ── Reset card UI: stepper → button ──────────────────────────────────
    // Only fully resets if no variants from this card remain staged
    resetCardUI(card) {
      card.classList.remove('kb-card--added');
      const atcBtn = card.querySelector('.kb-atc-btn');
      const stepper = card.querySelector('.kb-stepper');
      if (atcBtn) atcBtn.style.display = '';
      if (stepper) stepper.style.display = 'none';
    }

    // ── Sync card UI to currently selected variant's staged qty ───────────
    // Call this after variant change so stepper reflects the new variant
    syncCardUI(card) {
      const variantId = card.dataset.activeVariantId;
      const stagedEntry = variantId ? this.staged[variantId] : null;
      if (stagedEntry && stagedEntry.qty > 0) {
        this.updateCardUI(card, stagedEntry.qty);
      } else {
        this.resetCardUI(card);
      }
    }

    // ── Session storage persistence ──────────────────────────────────────
    saveToSession() {
      try {
        sessionStorage.setItem(
          this.storageKey,
          JSON.stringify(this.staged)
        );
      } catch(e) {
        // sessionStorage unavailable (private mode etc) — fail silently
      }
    }

    restoreFromSession() {
      try {
        const saved = sessionStorage.getItem(this.storageKey);
        if (!saved) return;
        const parsed = JSON.parse(saved);
        if (!parsed || typeof parsed !== 'object') return;

        // Validate each entry still has a valid variant ID before restoring
        // (products could have changed since session was saved)
        this.staged = parsed;

        // Rebuild card UIs to reflect restored state
        this.section.querySelectorAll('.kb-card').forEach(card => {
          this.syncCardUI(card);
        });

        this.renderSidebar();
        // Sync modal trigger button text to reflect restored selections
        this.syncModalTriggers();
        // Set layouts: move each slot to the restored product and re-render
        if (this.config.setMode && this.slots) {
          this.slots.forEach(s => s.syncFromStaged());
          this.renderSidebar();
        }
      } catch(e) {
        // Corrupt session data — clear and start fresh
        try { sessionStorage.removeItem(this.storageKey); } catch(e2) {}
      }
    }

    clearSession() {
      try {
        sessionStorage.removeItem(this.storageKey);
      } catch(e) {}
    }

    // ── Helpers ───────────────────────────────────────────────────────────
    getTotalQty() {
      return Object.values(this.staged).reduce((sum, item) => sum + item.qty, 0);
    }

    getTotalPrice() {
      return Object.values(this.staged).reduce((sum, item) => sum + (item.price * item.qty), 0);
    }

    getActiveTier() {
      const qty = this.getTotalQty();
      return this.config.tiers
        .filter(t => qty >= t.minQty)
        .sort((a, b) => b.minQty - a.minQty)[0] || null;
    }

    getNextTier() {
      const qty = this.getTotalQty();
      return this.config.tiers
        .filter(t => qty < t.minQty)
        .sort((a, b) => a.minQty - b.minQty)[0] || null;
    }

    // ── Base plus add-on helpers ──────────────────────────────────────────
    getBaseQty() {
      return Object.values(this.staged)
        .filter(item => item.role === 'base')
        .reduce((sum, item) => sum + item.qty, 0);
    }

    getAddonQty() {
      return Object.values(this.staged)
        .filter(item => item.role !== 'base')
        .reduce((sum, item) => sum + item.qty, 0);
    }

    hasBase() {
      return this.getBaseQty() > 0;
    }

    // -- Group-aware qualification (base_addon_flat) ------------------------
    // One rule feeds BOTH the button gate and the discount preview, so the two
    // never drift apart. Qualifies when every Base group holds at least one
    // item, and, if any Add-on groups exist, at least one Add-on item is staged.
    groupFilled(id) {
      return Object.values(this.staged).some(i => i.groupId === id && i.qty > 0);
    }

    getBaseGroupIds() {
      return Array.from(this.section.querySelectorAll('.kb-group[data-group-role="base"]'))
        .map(g => g.dataset.groupId).filter(Boolean);
    }

    hasGroups() {
      return this.section.querySelector('.kb-group') !== null;
    }

    hasAddonGroups() {
      return this.section.querySelector('.kb-group[data-group-role="addon"]') !== null;
    }

    baseGroupsAllFilled() {
      return this.getBaseGroupIds().every(id => this.groupFilled(id));
    }

    // Label of the first Base group with nothing staged, for the progress line.
    firstMissingBaseLabel() {
      const groups = this.section.querySelectorAll('.kb-group[data-group-role="base"]');
      for (const g of groups) {
        if (!this.groupFilled(g.dataset.groupId)) {
          return g.dataset.groupLabel || this.config.baseNoun;
        }
      }
      return '';
    }

    // First base group element with nothing staged, or null when all are filled.
    firstMissingBaseGroup() {
      const groups = this.section.querySelectorAll('.kb-group[data-group-role="base"]');
      for (const g of groups) {
        if (!this.groupFilled(g.dataset.groupId)) return g;
      }
      return null;
    }

    // Full-sentence empty-state button text for the first unfilled base group.
    // Merchant prompt (data-group-prompt) wins. Blank shows an editor-only flag
    // so a new person spots the gap, and a safe fallback on the live storefront.
    missingBaseButtonText() {
      const g = this.firstMissingBaseGroup();
      if (!g) return '';
      const prompt = (g.dataset.groupPrompt || '').trim();
      if (prompt) return prompt;
      const label = g.dataset.groupLabel || this.config.baseNoun;
      return this.config.designMode
        ? `[Set prompt: ${label}]`
        : `Add your ${label} to continue`;
    }

    qualifies() {
      // Legacy flat page with no groups: keep the old single-base rule.
      if (!this.hasGroups()) {
        return this.hasBase();
      }
      const baseFilled = this.baseGroupsAllFilled();
      const addonOk = !this.hasAddonGroups() || this.getAddonQty() > 0;
      return baseFilled && addonOk && this.getTotalQty() > 0;
    }

    // Discount preview qualifies on the shared rule in flat mode.
    qualifiesForDiscount() {
      if (this.config.discountModel === 'base_addon_flat') {
        return this.qualifies();
      }
      return true;
    }

    // Percentage the preview applies, per discount model.
    getEffectiveDiscountPct() {
      if (this.config.discountModel === 'base_addon_flat') {
        return this.qualifiesForDiscount() ? this.config.flatPct : 0;
      }
      const tier = this.getActiveTier();
      return tier ? tier.percentage : 0;
    }

    // Add to Cart gate reads the SAME rule as the discount in flat mode.
    // Quantity-tiers mode is unchanged.
    canAddToCart() {
      if (this.config.discountModel === 'base_addon_flat') {
        return this.qualifies();
      }
      return this.getTotalQty() >= Math.max(this.config.minItems, 1);
    }

    getStagedItems() {
      return Object.values(this.staged);
    }

    flashMessage(msg) {
      const el = this.section.querySelector('.kb-flash-message');
      if (!el) return;
      el.textContent = msg;
      el.classList.add('kb-flash-message--visible');
      setTimeout(() => el.classList.remove('kb-flash-message--visible'), 3000);
    }

    // ═══════════════════════════════════════════════════════════════════════
    // SIDEBAR RENDER — updates both desktop sidebar AND mobile drawer
    // ═══════════════════════════════════════════════════════════════════════
    renderSidebar() {
      const totalQty = this.getTotalQty();
      const totalPrice = this.getTotalPrice();
      const activeTier = this.getActiveTier();
      const nextTier = this.getNextTier();
      const items = this.getStagedItems();

      // Discount calc (model-aware: tiers ladder or base plus add-on flat)
      const flatMode = this.config.discountModel === 'base_addon_flat';
      const discountPct = this.getEffectiveDiscountPct();
      const discountAmount = Math.round(totalPrice * discountPct / 100);
      const finalPrice = totalPrice - discountAmount;

      // Progress bar logic
      const minItems = this.config.minItems;
      let progressPct;
      if (flatMode) {
        // Progress across every base group, plus one add-on step when add-on groups exist.
        const baseIds = this.getBaseGroupIds();
        const baseMet = baseIds.filter(id => this.groupFilled(id)).length;
        const addonReq = this.hasAddonGroups() ? 1 : 0;
        const addonMet = (addonReq && this.getAddonQty() > 0) ? 1 : 0;
        const totalReq = baseIds.length + addonReq;
        progressPct = totalReq
          ? Math.min(100, ((baseMet + addonMet) / totalReq) * 100)
          : (this.getTotalQty() > 0 ? 100 : 0);
      } else {
        const maxTierQty = this.config.tiers.length
          ? Math.max(...this.config.tiers.map(t => t.minQty))
          : minItems;
        const progressTarget = Math.max(maxTierQty, minItems, 1);
        progressPct = Math.min(100, (totalQty / progressTarget) * 100);
      }

      let progressMsg = '';
      if (flatMode) {
        const missingBase = this.firstMissingBaseLabel();
        if (missingBase) {
          progressMsg = `Add your ${missingBase} to start your bundle`;
        } else if (this.hasAddonGroups() && this.getAddonQty() === 0) {
          progressMsg = `Add one more item to get ${this.config.flatPct}% off`;
        } else {
          progressMsg = `${this.config.flatPct}% off unlocked`;
        }
      } else if (nextTier) {
        const needed = nextTier.minQty - totalQty;
        progressMsg = needed === 1
          ? `Add 1 more item to unlock ${nextTier.percentage}% off`
          : `Add ${needed} more items to unlock ${nextTier.percentage}% off`;
      } else if (activeTier) {
        progressMsg = `${activeTier.percentage}% discount unlocked! 🎉`;
      } else if (minItems > 0 && totalQty < minItems) {
        progressMsg = `Add ${minItems - totalQty} more item${minItems - totalQty !== 1 ? 's' : ''} to complete your bundle`;
      } else {
        progressMsg = totalQty > 0 ? 'Ready to add to cart!' : 'Start adding items below';
      }

      const itemsHtml = items.length === 0
        ? '<p class="kb-sidebar-empty">No items added yet.<br>Select products below.</p>'
        : items.map(item => `
            <div class="kb-sidebar-item">
              <div class="kb-sidebar-item-info">
                <span class="kb-sidebar-item-title">${item.title}${item.variantTitle ? ' — ' + item.variantTitle : ''}</span>
                <span class="kb-sidebar-item-qty">× ${item.qty}</span>
              </div>
              <span class="kb-sidebar-item-price">${formatMoney(item.price * item.qty)}</span>
            </div>
          `).join('');

      const canAdd = this.canAddToCart();
      let ctaText;
      if (canAdd) {
        ctaText = `Add ${totalQty} item${totalQty !== 1 ? 's' : ''} to Cart`;
      } else if (flatMode) {
        const basePrompt = this.missingBaseButtonText();
        if (basePrompt) {
          ctaText = basePrompt;
        } else if (this.hasAddonGroups() && this.getAddonQty() === 0) {
          ctaText = 'Add one more item to continue';
        } else {
          ctaText = 'Add to Cart';
        }
      } else if (!flatMode && this.config.minItems > 1) {
        ctaText = `Add ${this.config.minItems - totalQty} more to continue`;
      } else {
        ctaText = 'Add to Cart';
      }

      // Update ALL panels: desktop sidebar + mobile drawer
      // Pre-order aggregate (2026-07): any staged pre-order item flips the
      // CTA; message shows the LATEST ship date (bundle ships when all can).
      const preItems = items.filter(i => i.preorder && i.qty > 0);
      const hasPreorder = preItems.length > 0;
      let preorderShip = null, _preTs = 0;
      preItems.forEach(i => { const ts = parseShipDate(i.ship); if (ts > _preTs) { _preTs = ts; preorderShip = i.ship; } });
      if (canAdd && hasPreorder) {
        ctaText = 'Pre-order ' + totalQty + ' item' + (totalQty !== 1 ? 's' : '');
      }
      const preorderMsgHtml = hasPreorder
        ? '<div class="kb-preorder-msg">' + (preorderShip ? 'Pre-order — estimated shipping ' + preorderShip : 'Pre-order — shipping date to be confirmed') + '</div>'
        : '';

      const panels = [
        this.section.querySelector('.kb-sidebar'),
        this.section.querySelector('.kb-mobile-drawer')
      ].filter(Boolean);

      panels.forEach(panel => {
        const progressFill = panel.querySelector('.kb-progress-fill');
        const progressText = panel.querySelector('.kb-progress-text');
        const itemsList = panel.querySelector('.kb-sidebar-items');
        const subtotalEl = panel.querySelector('.kb-subtotal-value');
        const discountRow = panel.querySelector('.kb-discount-row');
        const discountValueEl = panel.querySelector('.kb-discount-value');
        const finalEl = panel.querySelector('.kb-final-value');
        const ctaBtn = panel.querySelector('.kb-finalise-btn');

        if (progressFill) progressFill.style.width = progressPct + '%';
        if (progressText) progressText.textContent = progressMsg;
        if (itemsList) itemsList.innerHTML = itemsHtml + preorderMsgHtml;
        if (subtotalEl) subtotalEl.textContent = formatMoney(totalPrice);
        if (discountRow) discountRow.style.display = discountAmount > 0 ? 'flex' : 'none';
        if (discountValueEl) discountValueEl.textContent = '−' + formatMoney(discountAmount);
        if (finalEl) finalEl.textContent = formatMoney(finalPrice);

        // Tier row active state
        panel.querySelectorAll('.kb-tier-row').forEach(row => {
          const rowPct = parseInt(row.dataset.tierPct || 0);
          row.classList.toggle('kb-tier-row--active', activeTier && rowPct === activeTier.percentage);
        });

        if (ctaBtn) {
          ctaBtn.disabled = !canAdd;
          ctaBtn.classList.toggle('kb-finalise-btn--ready', canAdd);
          ctaBtn.textContent = ctaText;
        }
      });

      // ── Mobile bar ──
      const countBadge = this.section.querySelector('.kb-mobile-count');
      if (countBadge) {
        countBadge.textContent = totalQty;
        countBadge.style.display = totalQty > 0 ? 'inline-flex' : 'none';
      }

      // Mobile bar guidance line. While the bundle does not yet qualify, show the
      // same empty-state prompt the sidebar and drawer buttons show, since the
      // compact mobile button has no room for a full sentence. Once it qualifies,
      // show the price.
      const mobileTotal = this.section.querySelector('.kb-mobile-total');
      if (mobileTotal) {
        if (this.config.flatDisplay && canAdd) {
          // Bundled total: discounted price, struck original, and a percent tag.
          // The "Bundled total:" label is hidden on mobile via CSS.
          const pct = this.config.flatPct;
          mobileTotal.classList.add('kb-mobile-total--summary');
          mobileTotal.innerHTML =
            '<span class="kb-bar-label">Bundled total: </span>' +
            '<span class="kb-bar-final">' + formatMoney(finalPrice) + '</span> ' +
            '<s class="kb-bar-original">' + formatMoney(totalPrice) + '</s>' +
            (pct > 0 ? ' <span class="kb-bar-tag">' + pct + '% OFF</span>' : '');
        } else {
          mobileTotal.classList.remove('kb-mobile-total--summary');
          mobileTotal.textContent = canAdd ? formatMoney(finalPrice) : ctaText;
        }
      }

      const mobileBar = this.section.querySelector('.kb-mobile-bar');
      if (mobileBar) mobileBar.classList.toggle('kb-mobile-bar--visible', totalQty > 0);

      const mobileFinaliseBtn = this.section.querySelector('.kb-finalise-btn--mobile');
      if (mobileFinaliseBtn) {
        mobileFinaliseBtn.disabled = !canAdd;
        mobileFinaliseBtn.classList.toggle('kb-finalise-btn--ready', canAdd);
        mobileFinaliseBtn.textContent = canAdd
          ? (hasPreorder ? `Pre-order ${totalQty} item${totalQty !== 1 ? 's' : ''}` : `Add ${totalQty} item${totalQty !== 1 ? 's' : ''} to cart`)
          : 'Add to cart';
      }

      // ── Set footer (in-flow, desktop) ──
      // Bundled total once qualified, the guidance line while not. The footer
      // button keeps its merchant label from Liquid; only its state changes.
      // The click binding and loading lock come via the shared class.
      const footerTotal = this.section.querySelector('.kb-set-footer-total');
      if (footerTotal) {
        if (canAdd) {
          footerTotal.classList.add('kb-set-footer-total--summary');
          footerTotal.innerHTML =
            '<span class="kb-bar-label">Bundled total: </span>' +
            '<span class="kb-bar-final">' + formatMoney(finalPrice) + '</span>' +
            (discountAmount > 0
              ? ' <s class="kb-bar-original">' + formatMoney(totalPrice) + '</s>' +
                (discountPct > 0 ? ' <span class="kb-bar-tag">' + discountPct + '% OFF</span>' : '')
              : '');
        } else {
          footerTotal.classList.remove('kb-set-footer-total--summary');
          footerTotal.textContent = ctaText;
        }
      }
      const footerBtn = this.section.querySelector('.kb-finalise-btn--footer');
      if (footerBtn) {
        // Capture the merchant label from Liquid once, then restore it on every
        // render. addToCart overwrites all finalise labels with "Adding...";
        // the panels and the mobile button get theirs rewritten above, and this
        // line does the same for the footer, or the label sticks after an add.
        if (!footerBtn.dataset.label) footerBtn.dataset.label = footerBtn.textContent.trim();
        footerBtn.textContent = footerBtn.dataset.label;
        footerBtn.disabled = !canAdd;
        footerBtn.classList.toggle('kb-finalise-btn--ready', canAdd);
      }
    }

    // ═══════════════════════════════════════════════════════════════════════
    // MOBILE DRAWER TOGGLE
    // ═══════════════════════════════════════════════════════════════════════
    initDrawerToggle() {
      const mobileBar = this.section.querySelector('.kb-mobile-bar');
      const mobileDrawer = this.section.querySelector('.kb-mobile-drawer');
      const drawerClose = this.section.querySelector('.kb-drawer-close');
      const drawerOverlay = this.section.querySelector('.kb-drawer-overlay');

      const openDrawer = () => {
        if (mobileDrawer) mobileDrawer.classList.add('kb-mobile-drawer--open');
        if (drawerOverlay) drawerOverlay.style.display = 'block';
        document.body.style.overflow = 'hidden';
      };

      const closeDrawer = () => {
        if (mobileDrawer) mobileDrawer.classList.remove('kb-mobile-drawer--open');
        if (drawerOverlay) drawerOverlay.style.display = 'none';
        document.body.style.overflow = '';
      };

      // Tapping the info area of the bar opens the drawer
      // Tapping the ATC button directly triggers cart add
      if (mobileBar) {
        mobileBar.addEventListener('click', (e) => {
          if (!e.target.closest('.kb-finalise-btn--mobile')) {
            openDrawer();
          }
        });
      }

      if (drawerClose) drawerClose.addEventListener('click', closeDrawer);
      if (drawerOverlay) drawerOverlay.addEventListener('click', closeDrawer);
    }

    // ═══════════════════════════════════════════════════════════════════════
    // ADD TO CART
    // ═══════════════════════════════════════════════════════════════════════
    initAddToCart() {
      // Both desktop sidebar and mobile drawer CTA buttons
      this.section.querySelectorAll('.kb-finalise-btn').forEach(btn => {
        btn.addEventListener('click', () => this.addToCart());
      });
    }

    async addToCart() {
      const items = this.getStagedItems();
      if (items.length === 0) return;

      const ctaBtns = this.section.querySelectorAll('.kb-finalise-btn');
      ctaBtns.forEach(btn => {
        btn.disabled = true;
        btn.classList.add('kb-finalise-btn--loading');
        btn.textContent = 'Adding...';
      });

      // Build items array — include _Bundle property for tracking
      const cartItems = items.map(item => {
        const props = {
          [this.config.bundleProperty]: this.config.collectionHandle || 'bundle',
          _bundle_role: item.role || 'addon',
        };
        // Pre-order (2026-07): property key + wording match STOQ's PDP flow
        // exactly ("Pre-order: Ships from {date}") so all add paths read the
        // same in cart, checkout and orders.
        if (item.preorder) props['Pre-order'] = item.ship ? ('Ships from ' + item.ship) : 'Yes';
        return { id: parseInt(item.id), quantity: item.qty, properties: props };
      });

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: cartItems })
        });

        const data = await response.json();

        if (data.status && data.status >= 400) {
          throw new Error(data.description || 'Could not add to cart');
        }

        // Success — clear staged state, reset UI and session
        this.clearSession();
        this.clearBundle();

        // Set layouts: re-fill each slot so the kit stays complete after adding.
        if (this.config.setMode && this.config.setPreselect && this.slots) {
          this.slots.forEach(s => s.stageActive());
        }

        // Clear the press/invert state so the reset button is not left inverted.
        ctaBtns.forEach(btn => btn.classList.remove('kb-finalise-btn--loading'));

        // Trigger Unicorn cart drawer
        document.dispatchEvent(new CustomEvent('cart:open'));
        document.dispatchEvent(new CustomEvent('cart:build'));

      } catch (err) {
        console.error('[KitBuilder] Cart add error:', err);
        this.flashMessage('Something went wrong. Please try again.');
        ctaBtns.forEach(btn => btn.classList.remove('kb-finalise-btn--loading'));
        // Restore button labels and enabled state from current staged data.
        this.renderSidebar();
      }
    }

    // ── Clear all staged items and reset card UIs ─────────────────────────
    clearBundle() {
      this.staged = {};
      this.section.querySelectorAll('.kb-card').forEach(card => this.syncCardUI(card));
      this.renderSidebar();
      // Close mobile drawer
      const mobileDrawer = this.section.querySelector('.kb-mobile-drawer');
      if (mobileDrawer) mobileDrawer.classList.remove('kb-mobile-drawer--open');
      document.body.style.overflow = '';
    }
  }

  // ═══════════════════════════════════════════════════════════════════════
  // ColourPickerModal — modal variant selector for a single card
  // ═══════════════════════════════════════════════════════════════════════
  class ColourPickerModal {
    constructor(card, bundleInstance) {
      this.card = card;
      this.bundle = bundleInstance;
      this.productId = card.dataset.productId;
      this.modal = card.querySelector('.kb-colour-modal');
      this.backdrop = card.closest('.kb-section').querySelector('.kb-modal-backdrop');
      this.mobileBar = card.closest('.kb-section').querySelector('.kb-mobile-bar');
      this.triggerBtn = card.querySelector('.kb-modal-trigger');

      if (!this.modal) return;

      this.init();
    }

    init() {
      // Open on trigger button
      if (this.triggerBtn) {
        this.triggerBtn.addEventListener('click', () => this.open());
      }

      // Close button inside modal
      const closeBtn = this.modal.querySelector('.kb-colour-modal-close');
      if (closeBtn) closeBtn.addEventListener('click', () => this.close());

      // Backdrop click closes
      if (this.backdrop) {
        this.backdrop.addEventListener('click', () => this.closeAll());
      }

      // Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !this.modal.hidden) this.close();
      });

      // Row steppers
      this.modal.querySelectorAll('.kb-modal-row').forEach(row => {
        const minus = row.querySelector('.kb-modal-minus');
        const plus = row.querySelector('.kb-modal-plus');
        if (minus) minus.addEventListener('click', () => this.changeRowQty(row, -1));
        if (plus) plus.addEventListener('click', () => this.changeRowQty(row, 1));
      });

      // Confirm button
      const confirmBtn = this.modal.querySelector('.kb-modal-confirm');
      if (confirmBtn) confirmBtn.addEventListener('click', () => this.confirm());
    }

    open() {
      // Sync modal row qtys from current staged state
      this.modal.querySelectorAll('.kb-modal-row').forEach(row => {
        const variantId = row.dataset.variantId;
        const staged = this.bundle.staged[variantId];
        const qtyEl = row.querySelector('.kb-modal-qty');
        if (qtyEl) qtyEl.textContent = staged ? staged.qty : 0;
        row.classList.toggle('kb-modal-row--selected', !!(staged && staged.qty > 0));
      });

      this.updateModalSummary();
      this.modal.hidden = false;
      if (this.backdrop) this.backdrop.classList.add('kb-modal-backdrop--visible');
      if (this.mobileBar) this.mobileBar.classList.add('kb-mobile-bar--suppressed');
      document.body.style.overflow = 'hidden';
    }

    close() {
      this.modal.hidden = true;
      if (this.backdrop) this.backdrop.classList.remove('kb-modal-backdrop--visible');
      if (this.mobileBar) this.mobileBar.classList.remove('kb-mobile-bar--suppressed');
      document.body.style.overflow = '';
    }

    closeAll() {
      // Close all open modals in this section
      this.card.closest('.kb-section').querySelectorAll('.kb-colour-modal').forEach(m => {
        m.hidden = true;
      });
      if (this.backdrop) this.backdrop.classList.remove('kb-modal-backdrop--visible');
      if (this.mobileBar) this.mobileBar.classList.remove('kb-mobile-bar--suppressed');
      document.body.style.overflow = '';
    }

    changeRowQty(row, delta) {
      const qtyEl = row.querySelector('.kb-modal-qty');
      if (!qtyEl) return;
      const current = parseInt(qtyEl.textContent) || 0;
      const newQty = Math.max(0, current + delta);
      qtyEl.textContent = newQty;
      row.classList.toggle('kb-modal-row--selected', newQty > 0);
      this.updateModalSummary();
    }

    // ── FIX v1.1: Confirm button always enabled so user can remove all items ──
    updateModalSummary() {
      let totalQty = 0;
      let totalPrice = 0;

      this.modal.querySelectorAll('.kb-modal-row').forEach(row => {
        const qty = parseInt(row.querySelector('.kb-modal-qty')?.textContent) || 0;
        const price = parseInt(row.dataset.variantPrice) || 0;
        totalQty += qty;
        totalPrice += qty * price;
      });

      const countEl = this.modal.querySelector('.kb-modal-summary-count');
      const priceEl = this.modal.querySelector('.kb-modal-summary-price');
      const confirmBtn = this.modal.querySelector('.kb-modal-confirm');

      if (countEl) {
        countEl.textContent = totalQty === 0
          ? 'No colours selected'
          : `${totalQty} item${totalQty !== 1 ? 's' : ''} selected`;
      }
      if (priceEl) priceEl.textContent = formatMoney(totalPrice);

      // Always enabled — zero is a valid state (remove all)
      // Label changes to "Remove All" when everything is zeroed out
      if (confirmBtn) {
        confirmBtn.disabled = false;
        confirmBtn.textContent = totalQty === 0 ? 'Remove All' : 'Confirm Selection';
        confirmBtn.classList.toggle('kb-modal-confirm--ready', totalQty > 0);
      }
    }

    confirm() {
      // Commit modal selections to bundle staged state
      this.modal.querySelectorAll('.kb-modal-row').forEach(row => {
        const variantId = row.dataset.variantId;
        const qty = parseInt(row.querySelector('.kb-modal-qty')?.textContent) || 0;

        if (qty > 0) {
          this.bundle.staged[variantId] = {
            id: variantId,
            qty: qty,
            price: parseInt(row.dataset.variantPrice) || 0,
            title: this.card.dataset.title || '',
            variantTitle: row.dataset.variantTitle || '',
            image: this.card.querySelector('.kb-card-img')?.src || '',
            role: this.card.dataset.groupRole || 'addon',
            groupId: this.card.dataset.groupId || '',
            preorder: row.dataset.preorder === 'true',
            ship: row.dataset.ship || '',
          };
        } else {
          // Remove if qty reduced to 0
          delete this.bundle.staged[variantId];
        }
      });

      // Update trigger button text to reflect selection
      this.updateTriggerButton();

      this.bundle.renderSidebar();
      this.bundle.saveToSession();
      this.close();
    }

    updateTriggerButton() {
      if (!this.triggerBtn) return;

      // Count staged variants belonging to this product
      const variants = this.card.dataset.variants ? JSON.parse(this.card.dataset.variants) : [];
      const variantIds = variants.map(v => String(v.id));
      const stagedForCard = variantIds
        .filter(id => this.bundle.staged[id] && this.bundle.staged[id].qty > 0)
        .map(id => this.bundle.staged[id]);

      if (stagedForCard.length === 0) {
        this.triggerBtn.textContent = 'Choose Colour →';
        this.triggerBtn.classList.remove('kb-modal-trigger--selected');
        this.card.classList.remove('kb-card--added');
      } else {
        const totalQty = stagedForCard.reduce((s, v) => s + v.qty, 0);
        const colourCount = stagedForCard.length;
        const label = colourCount === 1
          ? `✓ ${stagedForCard[0].variantTitle} · ${totalQty} unit${totalQty !== 1 ? 's' : ''} · Change`
          : `✓ ${colourCount} colours · ${totalQty} unit${totalQty !== 1 ? 's' : ''} · Change`;
        this.triggerBtn.textContent = label;
        this.triggerBtn.classList.add('kb-modal-trigger--selected');
        this.card.classList.add('kb-card--added');
      }
    }

    // Called on session restore to sync trigger button state
    syncFromStaged() {
      // Sync modal row quantities from staged state (for session restore)
      this.modal.querySelectorAll('.kb-modal-row').forEach(row => {
        const variantId = row.dataset.variantId;
        const staged = this.bundle.staged[variantId];
        const qtyEl = row.querySelector('.kb-modal-qty');
        if (qtyEl) qtyEl.textContent = staged ? staged.qty : 0;
        row.classList.toggle('kb-modal-row--selected', !!(staged && staged.qty > 0));
      });
      this.updateTriggerButton();
    }
  }

  // ═══════════════════════════════════════════════════════════════════════
  // SetSlot — one group rendered as a single-select slot with a cycler
  // ─────────────────────────────────────────────────────────────────────
  // Used only in set layouts (vertical / horizontal). Shows one product at a
  // time, cycles through the group's products with prev/next or dots, and
  // stages exactly ONE variant per group (single-select). Switching product or
  // colour replaces the group's staged entry rather than adding to it. The gate,
  // staged cart, sidebar and add-to-cart path are the shared engine, untouched.
  // ═══════════════════════════════════════════════════════════════════════
  class SetSlot {
    constructor(groupEl, builder) {
      this.builder = builder;
      this.group = groupEl;
      this.groupId = groupEl.dataset.groupId || '';
      // Controls live in the configurator card; the images live in the stage cell.
      this.configSlot = groupEl.querySelector('.kb-slot');
      this.el = this.configSlot;
      this.cards = this.configSlot ? Array.from(this.configSlot.querySelectorAll('.kb-card')) : [];
      this.stageCell = builder.section.querySelector(
        '.kb-stage-cell[data-group-id="' + this.groupId + '"]'
      );
      this.stageItems = this.stageCell
        ? Array.from(this.stageCell.querySelectorAll('.kb-stage-item'))
        : [];
      this.index = 0;
      this.init();
    }

    // Gather cycler buttons from both the config card and the stage cell,
    // so the shopper can browse products from either container.
    collectNav(sel) {
      const out = [];
      if (this.configSlot) this.configSlot.querySelectorAll(sel).forEach(b => out.push(b));
      if (this.stageCell) this.stageCell.querySelectorAll(sel).forEach(b => out.push(b));
      return out;
    }

    // Position the 3-up: prev peek, active centered, next peek, rest hidden.
    // Mirrors each visible item's image from its card so variant colour
    // changes show on the stage. Cycling WRAPS: with three or more products
    // the peek positions wrap around the ends, so both peeks are always
    // present and the last product peeks beside the first. Two products keep
    // a one-side peek but the arrows still wrap. One product renders alone
    // with both arrows disabled.
    renderStage() {
      if (!this.stageItems.length) return;
      const n = this.stageItems.length;
      const prevIdx = n >= 3 ? (this.index - 1 + n) % n : this.index - 1;
      const nextIdx = n >= 3 ? (this.index + 1) % n : this.index + 1;
      this.stageItems.forEach((item, i) => {
        item.classList.remove('kb-stage-item--prev', 'kb-stage-item--active', 'kb-stage-item--next');
        if (i === this.index) item.classList.add('kb-stage-item--active');
        else if (i === prevIdx) item.classList.add('kb-stage-item--prev');
        else if (i === nextIdx) item.classList.add('kb-stage-item--next');
        // Mirror the card's current (variant-swapped) image onto visible items.
        if (i === this.index || i === prevIdx || i === nextIdx) {
          const card = this.cards[i];
          const img = item.querySelector('.kb-stage-img');
          if (card && img && img.tagName === 'IMG') {
            const cardImg = card.querySelector('.kb-card-img');
            if (cardImg && cardImg.src && img.src !== cardImg.src) img.src = cardImg.src;
          }
        }
      });
      const canCycle = this.cards.length > 1;
      this.collectNav('.kb-slot-nav--prev').forEach(b => { b.disabled = !canCycle; });
      this.collectNav('.kb-slot-nav--next').forEach(b => { b.disabled = !canCycle; });
    }

    // Kept as an alias: the engine and older call sites refresh the stage
    // through this name.
    updateStageImage() {
      this.renderStage();
    }

    init() {
      if (!this.cards.length) return;

      const prevBtns = this.collectNav('.kb-slot-nav--prev');
      const nextBtns = this.collectNav('.kb-slot-nav--next');
      prevBtns.forEach(b => b.addEventListener('click', () => this.step(-1)));
      nextBtns.forEach(b => b.addEventListener('click', () => this.step(1)));

      // Click-to-select on the peeked stage items. Delegated to the track and
      // read at click time, since the position classes move between items.
      const track = this.stageCell ? this.stageCell.querySelector('.kb-stage-track') : null;
      if (track) {
        track.addEventListener('click', e => {
          const item = e.target.closest('.kb-stage-item');
          if (!item) return;
          if (item.classList.contains('kb-stage-item--prev')) this.step(-1);
          else if (item.classList.contains('kb-stage-item--next')) this.step(1);
        });
      }

      // Colour / size change on the active card re-stages it (single-select).
      this.cards.forEach((card, i) => {
        card.querySelectorAll('.kb-variant-input').forEach(input => {
          input.addEventListener('change', () => {
            if (i === this.index) {
              // The section inline script and initCard listeners have already
              // updated the hidden select; re-stage on the next tick so
              // syncCardState reads the freshly selected variant.
              setTimeout(() => this.stageActive(), 0);
            }
          });
        });
      });

      this.buildDots();
      this.show(0, false);
      // Start the slot filled so the kit reads as complete, like the references.
      if (this.builder.config.setPreselect) this.stageActive();
    }

    buildDots() {
      const dots = this.el.querySelector('.kb-slot-dots');
      if (!dots || this.cards.length < 2) return;
      dots.innerHTML = this.cards.map((c, i) =>
        '<button type="button" class="kb-slot-dot" data-i="' + i + '" aria-label="Option ' + (i + 1) + '"></button>'
      ).join('');
      dots.querySelectorAll('.kb-slot-dot').forEach(dot => {
        dot.addEventListener('click', () => this.show(parseInt(dot.dataset.i), true));
      });
    }

    // Show product at index i. The index WRAPS past either end (continuous
    // cycling), so stepping next from the last product lands on the first
    // and prev from the first lands on the last.
    // restage=true also updates the staged selection.
    show(i, restage) {
      if (!this.cards.length) return;
      const n = this.cards.length;
      this.index = n > 1 ? ((i % n) + n) % n : 0;
      this.cards.forEach((card, ci) => {
        card.classList.toggle('kb-card--slot-active', ci === this.index);
      });
      this.el.dataset.slotIndex = this.index;
      this.el.querySelectorAll('.kb-slot-dot').forEach((dot, di) => {
        dot.classList.toggle('kb-slot-dot--active', di === this.index);
      });
      this.renderStage();
      if (restage) this.stageActive();
    }

    step(delta) {
      this.show(this.index + delta, true);
    }

    // Stage the active card's current variant, replacing any prior entry for
    // this group so a slot never holds two products.
    stageActive() {
      const card = this.cards[this.index];
      if (!card) return;
      this.builder.syncCardState(card);
      const variantId = card.dataset.activeVariantId;

      // Single-select: drop any staged entry belonging to this group first.
      Object.keys(this.builder.staged).forEach(id => {
        if (this.builder.staged[id].groupId === this.groupId) {
          delete this.builder.staged[id];
        }
      });

      if (variantId && card.dataset.activeVariantAvailable !== 'false') {
        this.builder.staged[variantId] = this.builder.buildStagedEntry(card, 1);
      }

      this.updateStageImage();
      this.builder.renderSidebar();
      this.builder.saveToSession();
    }

    // On session restore, move the slot to the stored product and select its
    // stored variant, without staging again (state is already restored).
    syncFromStaged() {
      const entry = Object.values(this.builder.staged)
        .find(e => e.groupId === this.groupId);
      if (!entry) { this.show(0, false); return; }

      const idx = this.cards.findIndex(card => {
        const variants = JSON.parse(card.dataset.variants || '[]');
        return variants.some(v => String(v.id) === String(entry.id));
      });
      if (idx >= 0) {
        this.selectVariant(this.cards[idx], entry.id);
        this.show(idx, false);
      }
    }

    // Set a card's radios + hidden select to a specific variant id.
    selectVariant(card, variantId) {
      const variants = JSON.parse(card.dataset.variants || '[]');
      const v = variants.find(x => String(x.id) === String(variantId));
      if (!v) return;
      card.querySelectorAll('.kb-variant-input').forEach(input => {
        if (v.options.includes(input.value)) input.checked = true;
      });
      const select = card.querySelector('.kb-variant-select');
      if (select) {
        for (let i = 0; i < select.options.length; i++) {
          if (select.options[i].value === String(variantId)) {
            select.selectedIndex = i;
            break;
          }
        }
      }
      card.querySelectorAll('.kb-variant-selected-label').forEach(lbl => {
        const gi = lbl.dataset.optionIndex;
        if (gi != null && v.options[gi] != null) lbl.textContent = v.options[gi];
      });
      this.builder.syncCardState(card);
    }
  }

  // ═══════════════════════════════════════════════════════════════════════
  // BOOT — initialise all bundle sections on page
  // ═══════════════════════════════════════════════════════════════════════
  function boot() {
    document.querySelectorAll('.kb-section[data-section-id]').forEach(el => {
      if (!el.dataset.bbInitialised) {
        el.dataset.bbInitialised = 'true';
        new KitBuilder(el);
      }
    });
  }

  // ── Reliable multi-strategy boot ──────────────────────────────────────
  // Shopify pages with multiple sections can have timing issues where
  // DOMContentLoaded fires before all section HTML is rendered.
  // We boot on DOMContentLoaded, then again on load, then observe for
  // any late-rendering sections via MutationObserver.

  function safeBoot() {
    boot();
    // Re-run after a short delay to catch any sections rendered late
    setTimeout(boot, 300);
    setTimeout(boot, 800);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', safeBoot);
  } else {
    safeBoot();
  }

  // Shopify theme editor live reload
  document.addEventListener('shopify:section:load', boot);

  // MutationObserver — catch any sections added to DOM after initial load
  const observer = new MutationObserver(function(mutations) {
    let shouldBoot = false;
    mutations.forEach(function(mutation) {
      mutation.addedNodes.forEach(function(node) {
        if (node.nodeType === 1) {
          if (node.classList && node.classList.contains('kb-section')) shouldBoot = true;
          if (node.querySelector && node.querySelector('.kb-section')) shouldBoot = true;
        }
      });
    });
    if (shouldBoot) boot();
  });

  observer.observe(document.body, { childList: true, subtree: true });

})();